import React, { useEffect, useMemo, useState } from 'react'
import useGdap from '../hooks/useGdap'
import Navbar from '../components/Navbar'
import TenantTable from '../components/table/TenantTable'
import TenantRolesModal from '../components/TenantRolesModal'
import { minimumRoles } from '../config/OnboardingConfig'
import Spinner from '../components/Spinner'
import inforcerLogo from '../assets/inforcer-logo-rubik-new.svg'
import inforcerLogoDark from '../assets/inforcer-white-blue.svg'

const Assessment = () => {
  const { loading, error, customers: loadedCustomers, signInAndLoad, progress } = useGdap()

  const [customers, setCustomers] = useState([])
  const [panelOpen, setPanelOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState(null)
  
  useEffect(() => {
    setCustomers(Array.isArray(loadedCustomers) ? loadedCustomers : [])
  }, [loadedCustomers])

  const displayCustomers = useMemo(() => {
    return (customers || []).map(c => {
      const unifiedRoles = Array.isArray(c?.unifiedRoles) ? c.unifiedRoles : []
      const roleIds = new Set(
        unifiedRoles.map(r => r?.roleDefinitionId).filter(Boolean)
      )
      const tenantMissingRoles = minimumRoles
        .filter(r => !roleIds.has(r.id))
        .map(r => r.displayName)
      const name = c?.companyProfile?.companyName || ''
      const domain = c?.companyProfile?.domain || ''
      const tenantId = c?.companyProfile?.tenantId || c?.id || ''

      return {
        ...c,
        tenantMissingRoles,
        // Provide a normalized text value for GDAP status so sorting/filtering works
        gdapStatus:
          unifiedRoles.length > 0
            ? (tenantMissingRoles.length === 0
              ? 'Ready'
              : `Missing ${tenantMissingRoles.length}`)
            : 'No Relationship',
        // eligibleOnboarders already computed in mergeGroupsIntoCustomers
        eligibleOnboarders: Array.isArray(c?.eligibleOnboarders) ? c.eligibleOnboarders : [],
        searchText: `${name} ${domain} ${tenantId}`.toLowerCase(),
      }
    })
  }, [customers])

  const exportCsv = () => {
    const csvEscape = value => {
      const str = (value ?? '').toString()
      const escaped = str.replace(/\"/g, '\"\"')
      return `"${escaped}"`
    }
    const rows = []
    rows.push([
      'Tenant Name',
      'Primary Domain',
      'Tenant ID',
      'Tenant GDAP Status',
      'Security Group GDAP',
      'Auto Renewal',
      'Eligible Onboarders',
      'Missing Tenant Roles',
      'Missing Group Roles',
    ])
    ;(displayCustomers || []).forEach(t => {
      const name = t?.companyProfile?.companyName || ''
      const domain = t?.companyProfile?.domain || ''
      const tenantId = t?.companyProfile?.tenantId || t?.id || ''
      const unifiedRoles = Array.isArray(t?.unifiedRoles) ? t.unifiedRoles : []
      const missingTenant = Array.isArray(t?.tenantMissingRoles) ? t.tenantMissingRoles : []
      const gdapStatus =
        unifiedRoles.length > 0
          ? (missingTenant.length === 0 ? 'Ready' : `Missing ${missingTenant.length}`)
          : 'No Relationship'
      const groupStatus = t?.groupStatus || ''
      const autoRenewal = t?.autoExtendDuration === 'P180D' ? 'On' : 'Off'
      const eligible = (Array.isArray(t?.eligibleOnboarders) ? t.eligibleOnboarders : [])
        .map(u => (u.userPrincipalName || u.mail || u.displayName || '').toLowerCase())
        .filter(Boolean)
        .join('; ')
      const missingTenantRoles = missingTenant.join('; ')
      const missingGroupRoles = (Array.isArray(t?.groupMissingRoles) ? t.groupMissingRoles : []).join('; ')
      rows.push([
        name,
        domain,
        tenantId,
        gdapStatus,
        groupStatus,
        autoRenewal,
        eligible,
        missingTenantRoles,
        missingGroupRoles,
      ])
    })
    const csv = rows.map(r => r.map(csvEscape).join(',')).join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'tenants.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      {customers?.length > 0 && (
        <Navbar theme={'light'} setTheme={() => {}} realTheme={'light'} />
      )}

      <div className='container mx-auto px-4 lg:px-6 xl:px-8 py-8'>
        {loading && (
          <div className='flex min-h-screen items-center justify-center'>
            <div className='w-full max-w-xl'>
              <div className='flex justify-center mb-4'>
                <Spinner styles='border-4 border-gray-200 dark:border-slate-800 border-t-inforcer-cyan animate-spin' />
              </div>
              <div className='mb-2 flex items-center justify-between'>
                <p className='text-sm text-gray-600 dark:text-gray-300'>
                  {progress?.phase || 'Loading...'}
                </p>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  {Math.min(100, Math.max(0, Math.floor(progress?.percent || 0)))}%
                </p>
              </div>
              <div className='h-2 w-full rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden'>
                <div
                  className='h-full rounded-full bg-inforcer-cyan transition-[width] duration-500'
                  style={{ width: `${Math.min(100, Math.max(0, progress?.percent || 0))}%` }}
                />
              </div>
            </div>
          </div>
        )}
        {displayCustomers?.length === 0 && !loading && (
          <div className='flex min-h-screen items-center justify-center'>
            <div className='flex flex-col items-center gap-6 w-full max-w-md'>
              <div className='flex flex-col items-center gap-3'>
                <img src={inforcerLogo} alt='Inforcer' className='h-10 dark:hidden' />
                <img src={inforcerLogoDark} alt='Inforcer' className='h-10 hidden dark:inline' />
              </div>
              <div className='text-center'>
                <h1 className='text-xl font-semibold text-gray-900 dark:text-white'>Partner Center Companion (diet edition)</h1>
                <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Please sign in with your Partner Center Admin Agent account.</p>
              </div>
              <button
                type='button'
                onClick={() => signInAndLoad()}
                className='w-full inline-flex items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 hover:bg-gray-50 dark:bg-slate-800 dark:text-gray-100 dark:border-slate-700 dark:hover:bg-slate-700'
              >
                <span className='inline-flex -ml-1'>
                  {/* Microsoft 4-square */}
                  <svg width='20' height='20' viewBox='0 0 20 20' aria-hidden='true'>
                    <rect x='0' y='0' width='9' height='9' fill='#F35325' />
                    <rect x='11' y='0' width='9' height='9' fill='#81BC06' />
                    <rect x='0' y='11' width='9' height='9' fill='#05A6F0' />
                    <rect x='11' y='11' width='9' height='9' fill='#FFBA08' />
                  </svg>
                </span>
                <span className='font-medium'>Sign in with Microsoft</span>
              </button>
              {error && (
                <p className='text-sm text-red-600'>{error?.message || 'Error occurred'}</p>
              )}
            </div>
          </div>
        )}

        {displayCustomers?.length > 0 && (
          <>
            <div className='flex items-center justify-end mb-3'>
              <button
                type='button'
                onClick={exportCsv}
                className='inline-flex items-center gap-2 rounded-md bg-inforcer-cyan text-white px-3 py-1.5 text-sm hover:opacity-90'
              >
                {/* Download icon */ }
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4'>
                  <path d='M3 14.5A1.5 1.5 0 0 0 4.5 16h11a1.5 1.5 0 0 0 1.5-1.5V12a.5.5 0 0 0-1 0v2.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5V12a.5.5 0 0 0-1 0v2.5Z' />
                  <path d='M10 2a.5.5 0 0 1 .5.5v8.793l2.146-2.147a.5.5 0 1 1 .708.708l-3 3a.497.497 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L9.5 11.293V2.5A.5.5 0 0 1 10 2Z' />
                </svg>
                Export CSV
              </button>
            </div>
            <TenantTable
              data={displayCustomers}
              loading={loading}
              onSelectTenant={(t) => { setSelectedTenant(t); setPanelOpen(true) }}
            />
            <TenantRolesModal
              open={panelOpen}
              onClose={() => { setPanelOpen(false) }}
              tenant={selectedTenant}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default Assessment


